/**
 * A write-back on the canvas (Feature 71, 7.5): a dashed return arrow from the
 * writer back to its target, with a chip naming its writes. It leaves the
 * writer sideways into the gutter between columns, runs across below every
 * node, and comes up the gutter beside the target, so it crosses no node of an
 * arranged canvas and never reads as a link. It is drawn between the hidden
 * return handles every node carries, so it cannot be dragged into being;
 * clicking it selects the writer.
 */
import type { LinkageWrite } from '@optum/api-client/types';

import { BaseEdge, EdgeLabelRenderer, type Edge, type EdgeProps } from '../../ui/flow.js';
import type { ReturnEdge } from './draft.js';

export type WriteBackEdgeData = ReturnEdge['data'] & Record<string, unknown>;
export type WriteBackEdgeType = Edge<WriteBackEdgeData, 'writeBack'>;

// How far below the lower of the two nodes the arrow runs.
const HEAD = 6;
// From a node's bottom centre to the middle of the gutter beside it: half a
// node (184) and half the gap between arranged columns (260 - 184).
const TO_GUTTER = 130;
// How far below a node the arrow turns into the gutter.
const STEP_OUT = 12;

/** The chip's words: each op with the fields it writes, "append → answer, citations". */
export function chipFor(writes: readonly LinkageWrite[]): string {
  if (writes.length === 0) return 'no writes yet';
  const byOp = new Map<string, string[]>();
  for (const write of writes) byOp.set(write.op, [...(byOp.get(write.op) ?? []), write.field]);
  return [...byOp].map(([op, fields]) => `${op} → ${fields.join(', ')}`).join(' · ');
}

/**
 * The arrow's path from the writer's foot (`sx`, `sy`) to the target's foot
 * (`tx`, `ty`): out to the writer's right gutter, down to `floor`, across to
 * the target's left gutter, up, and in under the target. With its label point.
 */
export function returnPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  floor: number,
): [string, number, number] {
  const bottom = Math.max(floor, sy + STEP_OUT, ty + STEP_OUT);
  const out = sx + TO_GUTTER;
  const back = tx - TO_GUTTER;
  const line = [
    `M ${sx} ${sy}`,
    `V ${sy + STEP_OUT}`,
    `H ${out}`,
    `V ${bottom}`,
    `H ${back}`,
    `V ${ty + STEP_OUT}`,
    `H ${tx}`,
    `V ${ty}`,
  ].join(' ');
  const head = `M ${tx - HEAD} ${ty + HEAD} L ${tx} ${ty} L ${tx + HEAD} ${ty + HEAD}`;
  return [`${line} ${head}`, (out + back) / 2, bottom];
}

export function WriteBackEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
}: EdgeProps<WriteBackEdgeType>) {
  const [path, labelX, labelY] = returnPath(
    sourceX,
    sourceY,
    targetX,
    targetY,
    data?.floor ?? Math.max(sourceY, targetY),
  );
  return (
    <>
      <BaseEdge
        path={path}
        interactionWidth={14}
        style={{ stroke: 'var(--violet)', strokeWidth: 2, strokeDasharray: '6 5' }}
      />
      {data && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              padding: '2px 9px',
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--text-meta-lg)',
              fontWeight: 'var(--weight-semibold)',
              background: 'var(--violet-tint)',
              color: 'var(--violet)',
              whiteSpace: 'nowrap',
            }}
          >
            {chipFor(data.writes)}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
